import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { DelayedLinearProgress, SnackbarContext } from 'components';
import { isCheckboxChangeEvent } from 'lib/isChangeEvent';
import * as React from 'react';
import { useFetch } from '../../hooks';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(0, 0.5, 2, 0.5),
  },
  cardActions: {
    justifyContent: 'space-between',
  },
  cardHeader: {
    background: 'none',
    textAlign: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: 0,
  },
  cardHeaderTitle: {
    color: theme.palette.primary.main,
  },
  ssoCard: {
    padding: theme.spacing(2, 5, 2, 5),
    textAlign: 'center',
  },
}));

interface RegisterInput {
  password: string;
  authKey: string;
  clientName: string;
  clientEmail?: string;
  createDeviceUsers?: boolean;
}

interface ClientResponse {
  id: number;
  name: string;
  authKey: string;
}

interface UserResponse {
  username: string;
  password: string;
  note: string;
}

interface RegisterResponse {
  client: ClientResponse;
  users?: UserResponse[];
}

export const Register: React.FC = () => {
  const classes = useStyles();

  const { loading: fetchLoading, sendRequest, error } = useFetch();

  const [form, setForm] = React.useState<RegisterInput>(getInitialForm());
  const snackbar = React.useContext(SnackbarContext);

  const [registerResponse, setRegisterResponse] = React.useState<
    RegisterResponse | undefined
  >(undefined);

  return (
    <Card className={classes.card}>
      {registerResponse ? (
        <React.Fragment>
          <CardHeader
            className={classes.cardHeader}
            title="Registration Successful"
            classes={{ title: classes.cardHeaderTitle }}
          />
          <CardContent>
            <table
              cellPadding={5}
              width="100%"
              style={{ textAlign: 'left', width: '100%' }}
            >
              <tr>
                <th
                  style={{
                    width: '100px',
                  }}
                >
                  Client
                </th>
                <td>{registerResponse.client.name}</td>
              </tr>
              <tr>
                <th>Auth key</th>
                <td>{registerResponse.client.authKey}</td>
              </tr>
            </table>
            <br />
            <table cellPadding={5} width="100%">
              <tr>
                <th>Note</th>
                <th>Username</th>
                <th>Password</th>
              </tr>
              {registerResponse.users?.map((u) => (
                <tr>
                  <td>{u.note}</td>
                  <td>{u.username}</td>
                  <td>{u.password}</td>
                </tr>
              ))}
            </table>
            <Button
              type="button"
              fullWidth
              color="primary"
              onClick={() => {
                setForm({
                  authKey: '',
                  clientName: '',
                  clientEmail: '',
                  password: form.password,
                });
                setRegisterResponse(undefined);
              }}
            >
              Create another client
            </Button>
          </CardContent>
        </React.Fragment>
      ) : (
        <form onSubmit={submit}>
          <CardHeader
            className={classes.cardHeader}
            title="Talexio Device Manager"
            subheader="Register a new client account"
            classes={{ title: classes.cardHeaderTitle }}
          />

          <CardContent>
            <TextField
              fullWidth
              margin="normal"
              label="Client name"
              value={form.clientName}
              name="clientName"
              onChange={handleChange}
              type="text"
              autoComplete="clientName"
              autoFocus
              required
            />

            <TextField
              fullWidth
              margin="normal"
              value={form.authKey}
              label="Client auth key"
              name="authKey"
              onChange={handleChange}
              type="text"
              autoComplete="authKey"
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Client email"
              value={form.clientEmail}
              name="clientEmail"
              onChange={handleChange}
              type="email"
              autoComplete="clientEmail"
              helperText="This will be used for the CDM user of the client."
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="createDeviceUsers"
                  checked={form.createDeviceUsers}
                  onChange={handleChange}
                />
              }
              label="Create device users"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Password"
              name="password"
              value={form.password}
              onChange={handleChange}
              type="password"
              autoComplete="talexio-admin-password"
              helperText="Talexio security password to authorise this action"
              required
            />

            {error && (
              <Alert severity="error">
                <Typography variant="body1">{error.message}</Typography>
              </Alert>
            )}
          </CardContent>

          <CardActions className={classes.cardActions}>
            <Button
              type="submit"
              size="medium"
              color="primary"
              variant="contained"
            >
              Register
            </Button>
          </CardActions>
          <DelayedLinearProgress loading={fetchLoading} />
        </form>
      )}
    </Card>
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.stopPropagation();
    e.preventDefault();
    doRegister();
  }

  async function doRegister() {
    try {
      const {
        authKey,
        password,
        clientName,
        clientEmail,
        createDeviceUsers,
      } = form;

      if (!authKey || !clientName || !password) {
        return;
      }

      const response = await sendRequest('api/create-client', {
        postData: {
          password,
          clientEmail,
          clientName,
          authKey,
          createDeviceUsers: createDeviceUsers ? '1' : '0',
        },
      });

      const authResponse: RegisterResponse = await response.json();
      setRegisterResponse(authResponse);
    } catch (error) {
      snackbar.error(error);
    }
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    const { name, value } = e.currentTarget;

    const newVal = isCheckboxChangeEvent(e) ? e.currentTarget.checked : value;

    setForm((f) => ({ ...f, [name]: newVal }));
  }

  function getInitialForm() {
    return {
      authKey: '',
      clientName: '',
      password: '',
      createDeviceUsers: true,
    };
  }
};
